/**
 * Contato: a pessoa. Uma negociação pertence a um contato,
 * e um contato pode ter várias negociações.
 */
import { calcContractTotal } from './formato';
import { CONTA_COMO_PERDA, FORA_DO_PIPELINE } from './pipeline';

/** Junta o contato com as negociações dele e calcula os totais. */
export function comNegociacoes(contato, leads) {
  const meus = leads.filter(l => l.contact_id === contato.id);
  const ganhos   = meus.filter(l => l.stage === 'fechado');
  // Repescagem conta como perda no histórico (senão o win rate do contato sobe sozinho)
  // E segue aparecendo em 'ativos', porque voltou a ser trabalho. Só o dinheiro fica fora.
  const perdidos = meus.filter(l => CONTA_COMO_PERDA.includes(l.stage));
  const ativos   = meus.filter(l => !['fechado', 'perdido'].includes(l.stage));
  const noDinheiro = ativos.filter(l => !FORA_DO_PIPELINE.includes(l.stage));

  const soma = (arr, fn) => arr.reduce((s, l) => s + fn(l), 0);
  const decididos = ganhos.length + perdidos.length;

  return {
    ...contato,
    leads: meus,
    ativos, ganhos, perdidos,
    total: meus.length,
    pipeline: soma(noDinheiro, l => Number(l.value) || 0),
    receita: soma(ganhos, l => Number(l.value) || 0),
    receitaContrato: soma(ganhos, l => calcContractTotal(l.value, l.recurrence)),
    perdido: soma(perdidos, l => Number(l.value) || 0),
    winRate: decididos > 0 ? (ganhos.length / decididos) * 100 : null,
    ultimaAtividade: meus
      .map(l => l.updated_at || l.created_at)
      .filter(Boolean).sort().pop() || null,
    // cliente é quem já fechou pelo menos uma vez
    ehCliente: ganhos.length > 0,
  };
}

/** Monta a lista completa, ordenada por relevância comercial. */
export function montarLista(contatos, leads, { busca = '', filtro = 'todos' } = {}) {
  let lista = contatos.map(c => comNegociacoes(c, leads));

  if (filtro === 'clientes')    lista = lista.filter(c => c.ehCliente);
  if (filtro === 'ativos')      lista = lista.filter(c => c.ativos.length > 0);
  if (filtro === 'incompletos') lista = lista.filter(c => c.incompleto);
  if (filtro === 'multiplos')   lista = lista.filter(c => c.total > 1);

  const q = busca.toLowerCase().trim();
  if (q) {
    lista = lista.filter(c =>
      [c.nome, c.empresa, c.email, c.telefone, ...c.leads.map(l => l.company_name)]
        .join(' ').toLowerCase().includes(q));
  }

  // quem tem negócio aberto primeiro, depois por valor
  return lista.sort((a, b) =>
    (b.pipeline - a.pipeline) ||
    (b.receita - a.receita) ||
    (b.total - a.total) ||
    a.nome.localeCompare(b.nome));
}

/** Números do topo da tela. */
export function resumo(contatos, leads) {
  const lista = contatos.map(c => comNegociacoes(c, leads));
  const soma = (fn) => lista.reduce((s, c) => s + fn(c), 0);
  return {
    total: lista.length,
    clientes: lista.filter(c => c.ehCliente).length,
    comAtivo: lista.filter(c => c.ativos.length > 0).length,
    multiplos: lista.filter(c => c.total > 1).length,
    incompletos: lista.filter(c => c.incompleto).length,
    pipeline: soma(c => c.pipeline),
    receita: soma(c => c.receita),
  };
}

/** Leads que ainda não têm contato ligado (entraram depois da migração). */
export function semContato(leads) {
  return leads.filter(l => !l.contact_id);
}

// ─── Identidade: como um lead novo encontra (ou cria) o contato dele ───────
// Réplica de mcp-leads/migracao/logica.js. E-mail identifica melhor que telefone,
// que identifica melhor que nome. Nome só vale quando não há e-mail nem telefone.

export const normEmail = (s) => String(s || '').toLowerCase().trim();

/** Só dígitos, sem DDI 55, comparando pelos últimos 10 ou 11. */
export function normTelefone(s) {
  let d = String(s || '').replace(/\D/g, '');
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);
  return d.length >= 10 ? d.slice(-11) : '';
}

export const normNome = (s) => String(s || '').toLowerCase().trim().replace(/\s+/g, ' ');

/** Contato existente que corresponde aos dados do lead, ou null. */
export function acharContato(contatos, dados) {
  const e = normEmail(dados.contact_email);
  const t = normTelefone(dados.contact_phone);
  const n = normNome(dados.company_name);
  const lista = contatos || [];
  if (e) { const c = lista.find(c => c.email_norm === e); if (c) return c; }
  if (t) { const c = lista.find(c => c.telefone_norm === t); if (c) return c; }
  if (!e && !t && n) {
    const c = lista.find(c => normNome(c.empresa) === n || normNome(c.nome) === n);
    if (c) return c;
  }
  return null;
}

/** Documento de contato novo a partir dos dados de um lead. */
export function novoContato(dados, agora = new Date().toISOString()) {
  const email = String(dados.contact_email || '').trim();
  const telefone = String(dados.contact_phone || '').trim();
  const empresa = String(dados.company_name || '').trim();
  const pessoa = String(dados.contact_name || '').trim();
  return {
    nome: pessoa || empresa || 'Sem nome',
    empresa,
    email,
    telefone,
    email_norm: normEmail(email),
    telefone_norm: normTelefone(telefone),
    incompleto: !normEmail(email) && !normTelefone(telefone),
    origem: dados.origem || null,
    negociacoes: [],
    created_at: agora,
    updated_at: agora,
    _fonte: 'app',
  };
}

/** Campos que um contato existente ganha quando o lead novo traz o que faltava. */
export function complementoContato(contato, dados) {
  const patch = {};
  const email = String(dados.contact_email || '').trim();
  const telefone = String(dados.contact_phone || '').trim();
  if (email && !contato.email_norm) { patch.email = email; patch.email_norm = normEmail(email); }
  if (telefone && !contato.telefone_norm) { patch.telefone = telefone; patch.telefone_norm = normTelefone(telefone); }
  if ((patch.email_norm || contato.email_norm || patch.telefone_norm || contato.telefone_norm) && contato.incompleto) patch.incompleto = false;
  return patch;
}
